import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { canAccessRoute, getUserPermissions, isSuperAdmin } from '../utils/helpers';

/**
 * ProtectedRoute Component
 * Wraps routes that require specific permissions
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.permission - Required permission for access
 * @param {Array} props.permissions - Array of required permissions (any one)
 * @param {Array} props.allPermissions - Array of required permissions (all)
 * @param {string} props.redirectTo - Path to redirect to if access denied
 * @param {React.ReactNode} props.fallback - Component to show if access denied
 * @param {boolean} props.requireAuth - Whether authentication is required (default: true)
 */
const ProtectedRoute = ({ 
  children, 
  permission, 
  permissions = [], 
  allPermissions = [],
  redirectTo = '/404',
  fallback = null,
  requireAuth = true 
}) => {
  const authState = useSelector(state => state.auth);
  const userPermissions = getUserPermissions(authState);

  // Check if user is authenticated
  if (requireAuth && !authState.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Superadmin bypass - allow access to all routes
  if (isSuperAdmin(authState)) {
    return children;
  }

  // If no permissions required, allow access
  if (!permission && permissions.length === 0 && allPermissions.length === 0) {
    return children;
  }

  // Check single permission
  if (permission) {
    const hasAccess = canAccessRoute(authState, permission);
    if (!hasAccess) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  // Check any of the permissions
  if (permissions.length > 0) {
    const hasAccess = permissions.some(perm => canAccessRoute(authState, perm));
    if (!hasAccess) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  // Check all permissions
  if (allPermissions.length > 0) {
    const hasAccess = allPermissions.every(perm => canAccessRoute(authState, perm));
    if (!hasAccess) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  return children;
};

/**
 * Permission-based component wrapper
 * Shows/hides components based on user permissions
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.permission - Required permission
 * @param {Array} props.permissions - Array of required permissions (any one)
 * @param {Array} props.allPermissions - Array of required permissions (all)
 * @param {React.ReactNode} props.fallback - Component to show if no permission
 */
export const PermissionWrapper = ({ 
  children, 
  permission, 
  permissions = [], 
  allPermissions = [],
  fallback = null 
}) => {
  const authState = useSelector(state => state.auth);
  const userPermissions = getUserPermissions(authState);

  // Superadmin bypass - show all components
  if (isSuperAdmin(authState)) {
    return children;
  }

  // If no permissions required, show children
  if (!permission && permissions.length === 0 && allPermissions.length === 0) {
    return children;
  }

  // Check single permission
  if (permission) {
    const hasAccess = canAccessRoute(authState, permission);
    return hasAccess ? children : (fallback || null);
  }

  // Check any of the permissions
  if (permissions.length > 0) {
    const hasAccess = permissions.some(perm => canAccessRoute(authState, perm));
    return hasAccess ? children : (fallback || null);
  }

  // Check all permissions
  if (allPermissions.length > 0) {
    const hasAccess = allPermissions.every(perm => canAccessRoute(authState, perm));
    return hasAccess ? children : (fallback || null);
  }

  return children;
};

/**
 * Access Denied Component
 * Shows when user doesn't have required permissions
 */
export const AccessDenied = ({ message = "You don't have permission to access this page." }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    textAlign: 'center',
    padding: '2rem'
  }}>
    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
    <h2 style={{ color: '#e74c3c', marginBottom: '1rem' }}>Access Denied</h2>
    <p style={{ color: '#666', marginBottom: '2rem' }}>{message}</p>
    <button 
      onClick={() => window.history.back()} 
      style={{
        padding: '0.75rem 1.5rem',
        backgroundColor: '#3498db',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      Go Back
    </button>
  </div>
);

export default ProtectedRoute;
